<template>
  <div class="p-monitor">
    <!-- Header -->
    <div class="p-monitor__head">
      <div class="p-monitor__title-block">
        <div class="p-monitor__eyebrow">
          <span class="p-live-dot" /> 실시간 모니터링
          <span class="p-ws-badge" :class="wsConnected ? 'p-ws-badge--on' : 'p-ws-badge--off'">
            <span class="p-ws-badge__dot" />
            {{ wsConnected ? '실시간 연결됨' : '재연결 중 · 폴링 백업' }}
          </span>
        </div>
        <div class="p-monitor__title">{{ examData?.examName || '시험 모니터링' }}</div>
        <div class="p-monitor__sub">
          <code class="p-code">{{ examId || '-' }}</code>
        </div>
      </div>
      <div class="p-row-actions">
        <a-btn kind="ghost" size="sm" @click="refreshData">
          <template #icon>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
          </template>
          새로고침
        </a-btn>
        <a-btn v-if="!isClosing" kind="danger" size="sm" @click="showEndExamConfirm">시험 종료</a-btn>
        <a-btn v-else kind="danger" size="sm" @click="showForceEndConfirm">강제 종료</a-btn>
        <a-btn kind="ghost" size="sm" @click="$emit('back')">돌아가기</a-btn>
      </div>
    </div>

    <!-- KPI -->
    <div class="p-grid p-grid--kpi">
      <div class="p-kpi">
        <div class="p-kpi__head">
          <span class="p-kpi__label">총 참여 학생</span>
          <svg class="p-kpi__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
        </div>
        <div class="p-kpi__value-row">
          <span class="p-kpi__value">{{ totalStudents }}</span>
          <span class="p-kpi__unit">명</span>
        </div>
        <div class="p-kpi__foot p-kpi__foot--muted">응시자 명단 기준</div>
      </div>

      <div class="p-kpi p-kpi--warn">
        <div class="p-kpi__head">
          <span class="p-kpi__label">응시 중</span>
          <svg class="p-kpi__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
        </div>
        <div class="p-kpi__value-row">
          <span class="p-kpi__value">{{ activeStudents }}</span>
          <span class="p-kpi__unit">명</span>
        </div>
        <div class="p-kpi__foot">
          <span v-if="activeStudents > 0" class="p-kpi__chip">
            <span class="p-live-dot p-live-dot--warn" /> 진행 중
          </span>
          <span v-else class="p-kpi__foot--muted">대기</span>
        </div>
      </div>

      <div class="p-kpi p-kpi--ok">
        <div class="p-kpi__head">
          <span class="p-kpi__label">제출 완료</span>
          <svg class="p-kpi__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
        </div>
        <div class="p-kpi__value-row">
          <span class="p-kpi__value">{{ submittedStudents }}</span>
          <span class="p-kpi__unit">/ {{ totalStudents }} 명</span>
        </div>
        <div class="p-kpi__foot p-kpi__foot--muted">
          {{ submittedStudents === 0 ? '아직 제출 없음' : `${submittedStudents}명 제출됨` }}
        </div>
      </div>

      <div class="p-kpi p-kpi--info">
        <div class="p-kpi__head">
          <span class="p-kpi__label">제출률</span>
          <svg class="p-kpi__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
        </div>
        <div class="p-kpi__value-row">
          <span class="p-kpi__value">{{ submissionRate }}</span>
          <span class="p-kpi__unit">%</span>
        </div>
        <div class="p-kpi__bar">
          <div class="p-kpi__bar-fill" :style="{ width: submissionRate + '%' }" />
        </div>
      </div>
    </div>

    <!-- 마감 대기 안내 배너 -->
    <div v-if="isClosing" class="p-closing-banner">
      <div class="p-closing-banner__main">
        <span class="p-live-dot p-live-dot--warn" />
        <div class="p-closing-banner__text">
          <strong>마감 대기 중</strong>
          <span>미제출 {{ unsubmittedStudents }}명 · 전원 제출이 확인되면 자동으로 종료됩니다.</span>
        </div>
      </div>
      <a-btn kind="ghost" size="sm" :disabled="isSendingNotification" @click="showResendConfirm">다시 알림</a-btn>
    </div>

    <!-- 시험 진행 정보 -->
    <div class="p-timestrip">
      <div class="p-timestrip__item">
        <div class="p-timestrip__label">시험 시작</div>
        <div class="p-timestrip__value">{{ examData?.startTimeUTC ? formatCompactDateTime(examData.startTimeUTC) : '-' }}</div>
      </div>
      <div class="p-timestrip__divider" />
      <div class="p-timestrip__item">
        <div class="p-timestrip__label">예정 종료</div>
        <div class="p-timestrip__value">{{ examData?.endTimeUTC ? formatCompactDateTime(examData.endTimeUTC) : '-' }}</div>
      </div>
      <div class="p-timestrip__divider" />
      <div class="p-timestrip__item p-timestrip__item--accent">
        <div class="p-timestrip__label">남은 시간</div>
        <div class="p-timestrip__value p-timestrip__value--lg p-mono">{{ remainingTime }}</div>
      </div>
    </div>

    <!-- 학생 응시 현황 -->
    <a-card title="학생 응시 현황" :sub="`총 ${students.length}명`">
      <template #action>
        <a-pill kind="info">{{ students.length }}명</a-pill>
      </template>

      <div v-if="isInitialLoading" class="p-state">
        <div class="p-spinner" />
        <p>데이터를 불러오는 중...</p>
      </div>

      <a-empty v-else-if="students.length === 0" title="응시 학생이 없습니다" />

      <a-table v-else :columns="studentCols" :rows="students" row-key="users_idx">
        <template #cell-no="{ index }">
          <span class="p-mono">{{ index + 1 }}</span>
        </template>
        <template #cell-name="{ row }">
          <div class="p-cell-class">
            <div class="p-user-avatar">{{ (row.userName || row.name || '?').slice(0, 2) }}</div>
            <div>
              <div class="p-cell-title">{{ row.userName || row.name || '-' }}</div>
              <div class="p-cell-sub p-mono">{{ row.userEmail || row.email || '-' }}</div>
            </div>
          </div>
        </template>
        <template #cell-status="{ row }">
          <div class="p-status-cell">
            <a-pill :kind="statusPillKind(row.status)">{{ getStatusText(row.status) }}</a-pill>
            <a-pill v-if="row.attended" kind="info">출석</a-pill>
          </div>
        </template>
        <template #cell-leave="{ row }">
          <span :class="row.leaveCount ? 'p-leave-count' : 'p-cell-sub'">
            {{ row.leaveCount || 0 }}회
          </span>
        </template>
        <template #cell-submitted="{ row }">
          <a-pill :kind="row.submitted ? 'ok' : 'default'">
            {{ row.submitted ? '제출 완료' : '미제출' }}
          </a-pill>
        </template>
        <template #cell-elapsed="{ row }">
          <span class="p-mono">{{ row.elapsedTime || '-' }}</span>
        </template>
        <template #cell-score="{ row }">
          <span v-if="row.score !== undefined && row.score !== null" class="p-mono p-cell-title">{{ row.score }}점</span>
          <span v-else class="p-cell-sub">-</span>
        </template>
      </a-table>
    </a-card>

    <!-- 실시간 이벤트 피드 -->
    <a-card title="실시간 이벤트" sub="응시자 입장 · 이탈 · 답안 작성 · 제출">
      <template #action>
        <a-pill :kind="wsConnected ? 'ok' : 'warn'">{{ wsConnected ? '수신 중' : '대기' }}</a-pill>
      </template>

      <a-empty v-if="events.length === 0" title="아직 감지된 이벤트가 없습니다" />

      <ul v-else class="p-feed">
        <li v-for="(ev, i) in events" :key="i" class="p-feed__item">
          <span class="p-feed__time p-mono">{{ formatCompactDateTime(ev.timestamp) }}</span>
          <a-pill :kind="ev.kind">{{ ev.label }}</a-pill>
          <span class="p-feed__name">{{ ev.name }}</span>
        </li>
      </ul>
    </a-card>

    <!-- 시험 종료 확인 모달 (마감 대기 시작 · 제출 알림 다시 보내기 · 강제 종료) -->
    <a-modal
      v-if="showEndModal"
      :open="true"
      :title="modalHead.title"
      :sub="modalHead.sub"
      size="sm"
      @close="hideEndExamConfirm"
    >
      <!-- 강제 종료 모드: 즉시 완료 처리 -->
      <div v-if="modalMode === 'force'" class="p-end-confirm">
        <div v-if="unsubmittedStudents > 0" class="p-end-confirm__title">
          아직 제출하지 않은 학생이 {{ unsubmittedStudents }}명 있습니다.<br>
          그래도 지금 강제로 종료하시겠습니까?
        </div>
        <div v-else class="p-end-confirm__title">정말로 시험을 종료하시겠습니까?</div>
        <p class="p-end-confirm__desc">
          시험을 종료하면 모든 학생의 응시가 중단됩니다.<br>
          이 작업은 되돌릴 수 없습니다.
        </p>
      </div>
      <!-- 재발송 모드: 마감 대기 중 미제출자에게 제출 알림을 다시 보냄 (연속 발송 방지 확인창) -->
      <div v-else-if="modalMode === 'resend'" class="p-end-confirm">
        <div class="p-end-confirm__title">
          <template v-if="totalStudents > 0">미제출 학생 {{ unsubmittedStudents }}명에게 제출 요청 알림을 다시 보내시겠습니까?</template>
          <template v-else>응시자 명단이 비어 있어 응시자 전원(명단이 없으면 클래스 전원)에게 보냅니다. 다시 보내시겠습니까?</template>
        </div>
        <p class="p-end-confirm__desc">
          <template v-if="lastNotification">
            마지막 발송: {{ lastNotificationAgo }}<template v-if="lastNotification.count != null"> · {{ lastNotification.count }}명</template>
          </template>
          <template v-else>이 화면에서 아직 보낸 제출 알림이 없습니다.</template>
        </p>
      </div>
      <!-- 마감 대기 모드: 미제출자 알림 후 전원 제출 대기 -->
      <div v-else class="p-end-confirm">
        <div class="p-end-confirm__title">
          미제출 학생 {{ unsubmittedStudents }}명에게 제출 요청 알림을 보내고,<br>
          모두 제출하면 자동으로 종료됩니다.
        </div>
        <p class="p-end-confirm__desc">
          지금 바로 종료되지 않습니다. 전원 제출이 확인될 때까지 대기하며,<br>
          필요 시 "강제 종료"로 즉시 마감할 수 있습니다.
        </p>
      </div>
      <template #foot>
        <a-btn kind="ghost" @click="hideEndExamConfirm">취소</a-btn>
        <a-btn
          v-if="modalMode === 'force'"
          kind="danger"
          :disabled="isEndingExam"
          @click="endExam({ auto: false })"
        >
          {{ isEndingExam ? '종료 중...' : '강제 종료' }}
        </a-btn>
        <a-btn
          v-else-if="modalMode === 'resend'"
          kind="danger"
          :disabled="isSendingNotification"
          @click="resendNotification"
        >
          {{ isSendingNotification ? '보내는 중...' : '다시 보내기' }}
        </a-btn>
        <a-btn
          v-else
          kind="danger"
          :disabled="isEndingExam || isSendingNotification"
          @click="startClosing"
        >
          {{ isSendingNotification ? '알림 보내는 중...' : '알림 보내고 마감 대기' }}
        </a-btn>
      </template>
    </a-modal>
  </div>
</template>

<script>
import { computed, getCurrentInstance, onMounted, onUnmounted, ref, watch } from 'vue'
import { useExam } from '@/composables/useExam'
import { useExamMonitorSocket } from '@/composables/useExamMonitorSocket'
import { formatKoreanDateTime, formatUtcToTimeZone, parseUtc } from '@/utils/datetime'
import ACard from '@/components/Pineapple/ACard.vue'
import ATable from '@/components/Pineapple/ATable.vue'
import APill from '@/components/Pineapple/APill.vue'
import ABtn from '@/components/Pineapple/ABtn.vue'
import AEmpty from '@/components/Pineapple/AEmpty.vue'
import AModal from '@/components/Pineapple/AModal.vue'

export default {
  name: 'ExamMonitor',
  components: { ACard, ATable, APill, ABtn, AEmpty, AModal },
  props: {
    exam: {
      type: Object,
      default: null
    },
    examSession: {
      type: Object,
      default: null
    }
  },
  emits: ['back', 'exam-ended'],
  setup(props, { emit }) {
    const { proxy } = getCurrentInstance()
    const {
      fetchAttendance,
      fetchExamStudents,
      fetchExamClassroom
    } = useExam()

    const studentCols = [
      { key: 'no', label: '#', width: '60px' },
      { key: 'name', label: '학생' },
      { key: 'status', label: '응시 상태', width: '120px', align: 'center' },
      { key: 'leave', label: '이탈 횟수', width: '100px', align: 'center' },
      { key: 'submitted', label: '제출 여부', width: '130px', align: 'center' },
      { key: 'elapsed', label: '소요 시간', width: '120px', align: 'center' },
      { key: 'score', label: '점수', width: '100px', align: 'center' },
    ]

    const examId = computed(() => {
      if (props.exam) return props.exam.examId || props.exam._id
      if (props.examSession) return props.examSession.examId || props.examSession._id
      return null
    })

    const examData = computed(() => props.exam || props.examSession)
    const students = ref([])
    const isLoading = ref(false)
    const isInitialLoading = ref(true) // 초기 로딩과 리프레시 구분
    const refreshInterval = ref(null)
    const countdownInterval = ref(null)
    const now = ref(new Date())
    const classroomIdx = ref(null)
    const showEndModal = ref(false)
    const isEndingExam = ref(false)
    const isClosing = ref(false) // 마감 대기 모드 (미제출자 알림 후 전원 제출 대기)
    const modalMode = ref('close') // 'close': 알림 후 마감 대기 / 'resend': 제출 알림 다시 보내기 / 'force': 즉시 강제 종료
    // 제출 알림 전송 중 — 플랫폼은 같은 요청도 매번 다시 보내므로(중복 차단 없음) 버튼을 잠근다
    const isSendingNotification = ref(false)
    const lastNotification = ref(null) // 마지막 제출 알림 { at: Date, count: 보낸 학생 수 | null(명단 없이 전원) }

    const MODAL_HEAD = {
      close: { title: '시험 종료 확인', sub: '미제출 학생에게 제출 알림을 보냅니다' },
      resend: { title: '제출 알림 다시 보내기', sub: '미제출 학생에게 다시 보냅니다' },
      force: { title: '강제 종료 확인', sub: '시험을 종료하면 되돌릴 수 없습니다' }
    }
    const modalHead = computed(() => MODAL_HEAD[modalMode.value] || MODAL_HEAD.close)

    // 실시간 이벤트 피드 (WebSocket 수신)
    const events = ref([])
    const MAX_EVENTS = 100

    // WS 로 받은 실시간 상태를 이메일별로 보관 (폴링이 표를 새로 만들 때 이 위에 덮어씌움)
    // { [normalizedEmail]: { online, lastActivity, submittedViaWs } }
    const liveByEmail = ref({})

    // 모니터링 전용 WebSocket 연결 (/ws/exam-monitor/{examId})
    const { isConnected: wsConnected, lastMessage: wsMessage } = useExamMonitorSocket(examId.value)

    // UTC 시간을 타임존에 맞게 변환하여 표시
    const formatDateTime = (utcTime, timeZone) => {
      if (!utcTime) return ''
      const localTime = formatUtcToTimeZone(utcTime, timeZone || examData.value?.timeZone || 'Asia/Seoul')
      return formatKoreanDateTime(localTime)
    }

    // 컴팩트 포맷: "5/20 (수) 13:12"
    const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토']
    const formatCompactDateTime = (utcTime, timeZone) => {
      if (!utcTime) return '-'
      const localTime = formatUtcToTimeZone(utcTime, timeZone || examData.value?.timeZone || 'Asia/Seoul')
      const d = new Date(localTime)
      if (Number.isNaN(d.getTime())) return '-'
      const pad = (n) => String(n).padStart(2, '0')
      return `${d.getMonth() + 1}/${d.getDate()} (${WEEKDAYS[d.getDay()]}) ${pad(d.getHours())}:${pad(d.getMinutes())}`
    }

    // 통계 계산
    const totalStudents = computed(() => students.value.length)
    const activeStudents = computed(() => {
      return students.value.filter(s => !s.submitted && s.status === 'active').length
    })
    const submittedStudents = computed(() => {
      return students.value.filter(s => s.submitted).length
    })
    const unsubmittedStudents = computed(() => {
      return Math.max(0, totalStudents.value - submittedStudents.value)
    })
    const submissionRate = computed(() => {
      if (totalStudents.value === 0) return 0
      return Math.round((submittedStudents.value / totalStudents.value) * 100)
    })

    // 재발송 확인창의 "마지막 발송: N초 전"
    const lastNotificationAgo = computed(() => {
      if (!lastNotification.value) return ''
      const sec = Math.max(0, Math.floor((now.value - lastNotification.value.at) / 1000))
      return sec < 60 ? `${sec}초 전` : `${Math.floor(sec / 60)}분 전`
    })

    // 남은 시간 계산 (매초 갱신, HH:MM:SS 포맷)
    const remainingTime = computed(() => {
      if (!examData.value?.endTimeUTC) return '-'
      const end = parseUtc(examData.value.endTimeUTC)
      if (!end) return '-'
      const diff = end - now.value

      if (diff <= 0) return '종료됨'

      const hours = Math.floor(diff / 3_600_000)
      const minutes = Math.floor((diff % 3_600_000) / 60_000)
      const seconds = Math.floor((diff % 60_000) / 1000)
      const pad = (n) => String(n).padStart(2, '0')
      return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
    })

    // 상태 → Pineapple APill kind
    const statusPillKind = (status) => {
      if (status === 'active') return 'warn'
      if (status === 'submitted' || status === 'completed') return 'ok'
      if (status === 'disconnected') return 'err'
      return 'default'
    }

    // 상태 텍스트
    const getStatusText = (status) => {
      const texts = {
        'active': '응시 중',
        'submitted': '제출 완료',
        'completed': '완료',
        'disconnected': '이탈',
        'pending': '대기 중'
      }
      return texts[status] || '알 수 없음'
    }

    // 초 단위 → "X분 Y초" 포맷
    const formatTime = (seconds) => {
      if (!seconds) return '-'
      const minutes = Math.floor(seconds / 60)
      const secs = seconds % 60
      return `${minutes}분 ${secs}초`
    }

    // 이메일 정규화 (대소문자/공백 차이 흡수)
    const normalizeEmail = (email) => (email || '').trim().toLowerCase()

    // student_email 로 학생 행 찾기
    const findStudentByEmail = (email) => {
      const key = normalizeEmail(email)
      if (!key) return null
      return students.value.find(s => normalizeEmail(s.userEmail || s.email) === key) || null
    }

    // 실시간 상태 저장 (이메일별 누적 병합)
    const setLive = (email, patch) => {
      const key = normalizeEmail(email)
      if (!key) return
      liveByEmail.value[key] = { ...(liveByEmail.value[key] || {}), ...patch }
    }

    // 학생 행에 실시간 상태를 덮어씌움 (폴링 직후/WS 수신 직후 공통 사용)
    // 상태 머신: 제출완료 > 응시중(접속) > 이탈(접속했다 끊김) > 대기중(신호 없음)
    const applyLive = (row) => {
      if (!row) return row
      const live = liveByEmail.value[normalizeEmail(row.userEmail || row.email)]
      if (!live) return row
      if (row.submitted || live.submittedViaWs) {
        row.submitted = true
        row.status = 'submitted'
      } else if (live.online === true) {
        row.status = 'active'
      } else if (live.online === false) {
        row.status = 'disconnected'
      }
      if (live.online !== undefined) row.wsOnline = live.online
      if (live.lastActivity) row.lastActivity = live.lastActivity
      if (live.leaveCount != null) row.leaveCount = live.leaveCount
      if (live.lastLeftAt) row.lastLeftAt = live.lastLeftAt
      return row
    }

    // WS 이벤트 타입 → 표시 라벨
    const WS_EVENT_LABEL = {
      student_connected: '입장',
      student_disconnected: '이탈/연결 끊김',
      student_answer_update: '답안 작성',
      student_submitted: '제출'
    }

    // 피드에 이벤트 추가 (최신순, 상한 유지)
    const pushEvent = ({ email, name, kind, label, timestamp }) => {
      events.value.unshift({
        email,
        name: name || email || '알 수 없음',
        kind,
        label,
        timestamp: timestamp || new Date().toISOString()
      })
      if (events.value.length > MAX_EVENTS) {
        events.value = events.value.slice(0, MAX_EVENTS)
      }
    }

    // 모니터 WebSocket 수신 처리 — 백엔드가 보내는 실시간 상태를 즉시 그대로 반영한다.
    // (옛 소켓의 stale disconnect 억제는 백엔드 책임. 프론트는 시간 기반 추정 없이 이벤트를 신뢰.)
    const handleWsEvent = (msg) => {
      if (!msg || !msg.type) return
      const email = msg.student_email
      const row = findStudentByEmail(email)
      const name = row?.userName || row?.name || email
      const ts = msg.timestamp
      const key = normalizeEmail(email)

      switch (msg.type) {
        case 'student_connected': {
          // 접속/재접속 → 즉시 응시중.
          const wasActive = row?.status === 'active'
          setLive(email, { online: true })
          if (row && !row.submitted) {
            row.status = 'active'
          }
          // 이미 응시중이던 경우는 입장 피드 생략(중복 방지), 새 입장만 기록
          if (!wasActive) {
            pushEvent({ email, name, kind: 'info', label: WS_EVENT_LABEL.student_connected, timestamp: ts })
          }
          break
        }

        case 'student_disconnected': {
          // 이탈 즉시 반영 + 이탈 횟수 누적 (제출 완료자는 정상 종료라 횟수 제외)
          const suspicious = !row?.submitted
          const prevCount = (key && liveByEmail.value[key]?.leaveCount) || 0
          const leaveCount = suspicious ? prevCount + 1 : prevCount
          setLive(email, { online: false, leaveCount, lastLeftAt: ts })
          pushEvent({
            email,
            name,
            kind: suspicious ? 'warn' : 'info',
            label: WS_EVENT_LABEL.student_disconnected,
            timestamp: ts
          })
          if (suspicious && proxy.$notifyVue) {
            proxy.$notifyVue({ type: 'warn', title: `${name} 응시자 이탈 (누적 ${leaveCount}회)` })
          }
          break
        }

        case 'student_answer_update':
          // 답안 작성 = 접속해 응시 중이라는 확실한 증거 → 즉시 응시중(피드에는 기록 안 함).
          setLive(email, { online: true, lastActivity: ts })
          if (row && !row.submitted) {
            row.status = 'active'
          }
          break

        case 'student_submitted':
          setLive(email, { submittedViaWs: true, online: false })
          pushEvent({ email, name, kind: 'ok', label: WS_EVENT_LABEL.student_submitted, timestamp: ts })
          break

        case 'monitor_init': {
          // 연결 시점 스냅샷: 모니터를 열기 전부터 이미 접속 중인 학생을 즉시 응시중으로 반영한다.
          // (이게 없으면 그런 학생은 새 이벤트가 올 때까지 '대기중'에 갇힌다.)
          const list = Array.isArray(msg.students) ? msg.students : []
          let applied = 0
          list.forEach((s) => {
            const e = s?.student_email || s?.email || s?.learner_email || (typeof s === 'string' ? s : null)
            if (!e) return
            setLive(e, { online: true })
            const r = findStudentByEmail(e)
            if (r) applyLive(r)
            applied++
          })
          console.log('[ExamMonitor WS] monitor_init 적용', applied, '명 / students=', JSON.stringify(msg.students))
          break
        }

        default:
          // 그 외 미지원 타입 — 구조 파악용 전체 덤프
          console.log('[ExamMonitor WS] (unhandled)', msg.type, JSON.stringify(msg))
          break
      }

      // 현재 표에 즉시 반영 (폴링을 기다리지 않고 바로 갱신)
      if (row) applyLive(row)

      // 진단용: 온라인 플래그 + 행 매칭/최종 상태까지 출력.
      // rowFound=false 면 이메일 매칭 실패(행을 못 찾아 상태가 안 바뀜)를 의미한다.
      console.log('[ExamMonitor WS]', msg.type, email, 'ts=', ts,
        '→ online=', liveByEmail.value[normalizeEmail(email)]?.online,
        '| rowFound=', !!row,
        '| rowStatus=', row?.status,
        '| rosterEmails=', students.value.map(s => s.userEmail || s.email))
    }

    // flush:'sync' — WS 메시지마다 즉시 동기 처리한다.
    // 기본(비동기) watch 는 같은 틱에 두 메시지가 연달아 오면 마지막 값만 콜백에 전달되어
    // 첫 이벤트(첫 접속/첫 이탈)가 유실될 수 있다. 동기 플러시로 모든 메시지를 빠짐없이 처리.
    watch(wsMessage, (msg) => handleWsEvent(msg), { flush: 'sync' })

    // 데이터 새로고침
    const refreshData = async () => {
      if (!examId.value) {
        console.warn('ExamMonitor: examId is missing')
        isInitialLoading.value = false
        return
      }

      //console.log('ExamMonitor: Refreshing data for exam:', examId.value)

      // 초기 로딩이 아닐 때는 isLoading을 false로 유지 (깜빡임 방지)
      if (isInitialLoading.value) {
        isLoading.value = true
      }

      try {
        // 시험 정보는 props에서 받아오므로 별도 업데이트 불필요

        // 클래스 정보 가져오기
        if (!classroomIdx.value) {
          try {
            //console.log('ExamMonitor: Fetching classroom info...')
            const classroom = await fetchExamClassroom(examId.value)
            //console.log('ExamMonitor: Classroom data:', classroom)
            if (classroom) {
              classroomIdx.value = classroom.idx
            }
          } catch (error) {
            console.error('ExamMonitor: Error fetching classroom:', error)
          }
        }

        //console.log('ExamMonitor: Fetching attendance and students...')
        // 출석 정보, 학생 목록, 응시/제출 결과를 병렬 조회
        const [attendanceList, studentsList, examAnswersResponse] = await Promise.all([
          fetchAttendance(examId.value),
          classroomIdx.value ? fetchExamStudents(classroomIdx.value, examId.value) : Promise.resolve([]),
          proxy.$axios({
            method: 'get',
            url: `/exam-answers/exam/${examId.value}`,
            api_url: proxy.$CONF.ACHIEVE_API_URL
          }).catch(err => {
            console.error('ExamMonitor: Error fetching exam answers:', err)
            return null
          })
        ])

        // 출석 정보를 Map으로 변환
        const attendanceMap = new Map()
        if (Array.isArray(attendanceList)) {
          attendanceList.forEach(att => {
            if (att.users_idx !== undefined) {
              attendanceMap.set(att.users_idx, true)
            }
          })
        }

        // 응시 결과를 이메일 키 Map으로 변환 (WS 처리와 같이 정규화 — 제출자가 미제출로 남아 제출 알림 대상에 끼지 않게)
        const resultMap = new Map()
        const examAnswerStudents = examAnswersResponse?.data?.students || []
        examAnswerStudents.forEach(r => {
          if (r.learner_email) {
            resultMap.set(normalizeEmail(r.learner_email), r)
          }
        })
        const submittedStatuses = ['submitted', 'ai_graded', 'graded']
        // 진행 중 답안 레코드가 있으면 = 이미 응시를 시작한 학생.
        // WS 접속 이벤트를 받기 전(모니터를 늦게 열었거나 학생이 먼저 접속한 경우)에도
        // 폴링만으로 '응시중'을 판정해 대기중에 갇히지 않도록 한다.
        const inProgressStatuses = ['in_progress', 'active', 'solving']

        // 학생 목록에 출석 / 제출 / 점수 / 소요 시간 매핑
        students.value = (studentsList || []).map(student => {
          const isAttended = attendanceMap.has(student.users_idx)
          const email = student.userEmail || student.email
          const result = email ? resultMap.get(normalizeEmail(email)) : null
          const submitted = result ? submittedStatuses.includes(result.status) : false
          const inProgress = result ? inProgressStatuses.includes(result.status) : false

          const row = {
            ...student,
            // 제출완료 > (진행 중 레코드 있으면)응시중 > 대기중.
            // 이후 applyLive 가 WS 실시간 상태(이탈 등)로 덮어쓴다.
            status: submitted ? 'submitted' : (inProgress ? 'active' : 'pending'),
            submitted,
            attended: isAttended, // 대면 출석 정보는 별도 표식으로만 표시
            score: result?.totalScore ?? null,
            elapsedTime: result?.time_taken ? formatTime(result.time_taken) : '-',
            leaveCount: 0 // applyLive 가 WS 누적 이탈 횟수로 덮어씀
          }
          // WS 로 받은 실시간 상태(접속/이탈/제출)를 폴링 결과 위에 덮어씌움
          return applyLive(row)
        })

        console.log('ExamMonitor: Data refresh completed. Students count:', students.value.length)

        // 마감 대기 모드에서 전원 제출이 확인되면 확인창 없이 자동 종료
        if (
          isClosing.value &&
          !isEndingExam.value &&
          totalStudents.value > 0 &&
          unsubmittedStudents.value === 0
        ) {
          await endExam({ auto: true })
        }
      } catch (error) {
        console.error('ExamMonitor: Error refreshing data:', error)
      } finally {
        isLoading.value = false
        isInitialLoading.value = false
      }
    }

    const showEndExamConfirm = () => { modalMode.value = 'close'; showEndModal.value = true }
    const showForceEndConfirm = () => { modalMode.value = 'force'; showEndModal.value = true }
    const showResendConfirm = () => {
      if (isSendingNotification.value) return
      modalMode.value = 'resend'
      showEndModal.value = true
    }
    const hideEndExamConfirm = () => {
      if (!isEndingExam.value && !isSendingNotification.value) showEndModal.value = false
    }

    // 제출 요청 푸시(exam_submit_request) 발송 — 앱은 시험 화면에서 받으면 바로 답안을 제출한다.
    // 명단이 있으면 미제출자 users_idx 만 target_users_idx 로 보낸다(제출 판정은 refreshData 와 같음).
    // 명단을 못 받았으면(빈 배열) 필드를 빼서 플랫폼 기본값(응시자 명단 전원, 명단이 없으면 클래스 전원)으로 보낸다.
    // 플랫폼은 같은 요청도 매번 다시 보내므로 전송 중에는 잠그고, 다시 보낼 때는 확인창을 거친다.
    // 계약: docs/FCM_푸시알림_매뉴얼.md 3.2-1
    const sendUnsubmittedNotification = async () => {
      if (!examId.value || isSendingNotification.value) return false
      isSendingNotification.value = true

      try {
        // 클래스 정보가 아직 없으면 보강
        if (!classroomIdx.value) {
          try {
            const classroom = await fetchExamClassroom(examId.value)
            if (classroom) classroomIdx.value = classroom.idx
          } catch (error) {
            console.error('ExamMonitor: Error fetching classroom for notification:', error)
          }
        }

        if (!classroomIdx.value) {
          console.warn('ExamMonitor: classroomIdx 가 없어 제출 알림을 발송하지 않습니다.')
          return false
        }

        const hasRoster = students.value.length > 0
        const unsubmittedRows = students.value.filter(s => !s.submitted)
        if (hasRoster && unsubmittedRows.length === 0) {
          if (proxy.$notifyVue) {
            proxy.$notifyVue({ type: 'info', title: '미제출 학생이 없어 제출 알림을 보내지 않았습니다.' })
          }
          return false
        }
        // users_idx 가 없는 미제출 행이 있으면 대상을 좁힐 수 없으니 종전처럼 전원에게 보낸다
        const targetUsersIdx = hasRoster && unsubmittedRows.every(s => s.users_idx != null)
          ? unsubmittedRows.map(s => s.users_idx)
          : null

        const examName = examData.value?.examName
        const response = await proxy.$axios({
          method: 'post',
          url: '/notification/send-exam-notification/',
          data: {
            title: '시험 종료 안내',
            body: examName
              ? `"${examName}" 시험이 종료되어 답안이 제출되었습니다.`
              : '시험이 종료되어 답안이 제출되었습니다.',
            classroom_idx: classroomIdx.value,
            exam_id: examId.value,
            // 앱(플러터)이 "제출 요청" 푸시임을 식별하기 위한 구분자
            data: {
              type: 'exam_submit_request',
              action: 'submit',
              examId: String(examId.value)
            },
            ...(targetUsersIdx ? { target_users_idx: targetUsersIdx } : {})
          }
        })

        // 응시자 명단 밖 idx 는 플랫폼이 보내지 않고 ignored_count 로 센다
        const ignored = Number(response?.data?.ignored_count) || 0
        if (ignored > 0) {
          console.warn('ExamMonitor: 응시자 명단에 없어 제외된 학생', ignored, '명')
        }
        const sentCount = targetUsersIdx ? Math.max(0, targetUsersIdx.length - ignored) : null
        lastNotification.value = { at: new Date(), count: sentCount }

        if (proxy.$notifyVue) {
          proxy.$notifyVue({
            type: 'success',
            title: sentCount != null
              ? `미제출 학생 ${sentCount}명에게 제출 알림을 보냈습니다.`
              : '응시자에게 제출 알림을 보냈습니다.',
            text: ignored > 0 ? `응시자 명단에 없는 ${ignored}명은 제외됐습니다.` : undefined
          })
        }
        return true
      } catch (error) {
        console.error('ExamMonitor: Error sending submit notification:', error)
        if (proxy.$notifyVue) {
          proxy.$notifyVue({ type: 'warn', title: '제출 알림 발송에 실패했습니다.' })
        }
        return false
      } finally {
        isSendingNotification.value = false
      }
    }

    // 마감 대기 중 「다시 알림」 → 확인창 → 미제출자에게 다시 보냄
    const resendNotification = async () => {
      await sendUnsubmittedNotification()
      showEndModal.value = false
    }

    // 마감 대기 진입: 미제출자에게 알림 발송 후 전원 제출 대기 (즉시 종료하지 않음)
    const startClosing = async () => {
      if (!examId.value) {
        console.error('ExamMonitor: examId is missing')
        return
      }
      if (isSendingNotification.value || isEndingExam.value) return // 연타 방지

      // 클릭 시점에 이미 전원 제출이면 알림 생략하고 즉시 종료
      if (unsubmittedStudents.value === 0 && totalStudents.value > 0) {
        showEndModal.value = false
        await endExam({ auto: false })
        return
      }

      if (unsubmittedStudents.value > 0) {
        await sendUnsubmittedNotification()
      }

      isClosing.value = true
      showEndModal.value = false
      if (proxy.$notifyVue) {
        proxy.$notifyVue({
          type: 'success',
          title: '마감 대기를 시작했습니다.',
          text: '전원 제출이 확인되면 시험이 자동으로 종료됩니다.'
        })
      }
    }

    // 시험 종료 처리
    const endExam = async ({ auto = false } = {}) => {
      if (!examId.value) {
        console.error('ExamMonitor: examId is missing')
        return
      }
      if (isEndingExam.value) return // 중복 호출 방지

      isEndingExam.value = true

      try {
        const response = await proxy.$axios({
          method: 'patch',
          url: `/exams/${examId.value}/status`,
          params: { status: 'completed' },
          api_url: proxy.$CONF.ACHIEVE_API_URL,
          loadingBar: true
        })

        if (response) {
          isClosing.value = false
          showEndModal.value = false
          if (proxy.$notifyVue) {
            proxy.$notifyVue({
              type: 'success',
              title: auto ? '모든 학생이 제출하여 시험이 자동 종료되었습니다.' : '시험이 종료되었습니다.'
            })
          }
          emit('exam-ended')
          emit('back')
        }
      } catch (error) {
        console.error('ExamMonitor: Error ending exam:', error)
        if (proxy.$notifyVue) {
          proxy.$notifyVue({ type: 'error', title: '시험 종료 중 오류가 발생했습니다.' })
        }
      } finally {
        isEndingExam.value = false
      }
    }

    // 자동 새로고침 설정
    onMounted(() => {
      console.log('[ExamMonitor] mounted. examId=', examId.value, 'wsConnected=', wsConnected.value)
      refreshData()
      // 10초마다 자동 새로고침
      refreshInterval.value = setInterval(() => {
        refreshData()
      }, 10000)
      // 1초마다 카운트다운 갱신
      countdownInterval.value = setInterval(() => {
        now.value = new Date()
      }, 1000)
    })

    onUnmounted(() => {
      if (refreshInterval.value) {
        clearInterval(refreshInterval.value)
      }
      if (countdownInterval.value) {
        clearInterval(countdownInterval.value)
      }
    })

    return {
      examId,
      examData,
      students,
      studentCols,
      isLoading,
      isInitialLoading,
      totalStudents,
      activeStudents,
      submittedStudents,
      unsubmittedStudents,
      submissionRate,
      remainingTime,
      formatDateTime,
      formatCompactDateTime,
      statusPillKind,
      getStatusText,
      wsConnected,
      events,
      refreshData,
      showEndModal,
      isEndingExam,
      isClosing,
      modalMode,
      modalHead,
      isSendingNotification,
      lastNotification,
      lastNotificationAgo,
      showEndExamConfirm,
      showForceEndConfirm,
      showResendConfirm,
      hideEndExamConfirm,
      sendUnsubmittedNotification,
      resendNotification,
      startClosing,
      endExam
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/_pineapple.scss';

.p-monitor {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
}

.p-monitor__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 20px;
  background: $p-panel;
  border: 1px solid $p-line;
  border-radius: 12px;
}
.p-monitor__title-block {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
.p-monitor__eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 800;
  color: $p-err;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.p-monitor__title {
  font-size: 18px;
  font-weight: 800;
  color: $p-ink;
}
.p-monitor__sub {
  font-size: 12px;
  color: $p-ink-mid;
}

.p-row-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.p-grid {
  display: grid;
  gap: 12px;
}
.p-grid--kpi {
  grid-template-columns: repeat(4, minmax(0, 1fr));
  @media (max-width: 768px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

/* KPI Cards */
.p-kpi {
  position: relative;
  background: $p-panel;
  border: 1px solid $p-line;
  border-radius: 12px;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 124px;
  overflow: hidden;
  transition: border-color 0.15s ease, transform 0.15s ease;

  &::before {
    content: '';
    position: absolute;
    left: 0; top: 0; bottom: 0;
    width: 3px;
    background: $p-line-hard;
  }
  &:hover { border-color: $p-line-hard; transform: translateY(-1px); }

  &--warn::before { background: $p-warn; }
  &--ok::before { background: $p-ok; }
  &--info::before { background: $p-point-deep; }

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }
  &__label {
    font-size: 11px;
    font-weight: 800;
    color: $p-ink-mid;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  &__icon {
    width: 16px;
    height: 16px;
    color: $p-ink-soft;
    flex-shrink: 0;
  }
  &__value-row {
    display: flex;
    align-items: baseline;
    gap: 4px;
  }
  &__value {
    font-family: $p-font-mono;
    font-size: 28px;
    font-weight: 800;
    color: $p-ink;
    line-height: 1.1;
  }
  &__unit {
    font-size: 12px;
    color: $p-ink-mid;
    font-weight: 600;
  }
  &__foot {
    font-size: 11px;
    color: $p-ink;
    min-height: 14px;
    display: flex;
    align-items: center;

    &--muted { color: $p-ink-soft; }
  }
  &__chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 10px;
    font-weight: 800;
    color: $p-ink-mid;
  }
  &__bar {
    height: 4px;
    background: $p-line-soft;
    border-radius: 999px;
    overflow: hidden;
    margin-top: 2px;
  }
  &__bar-fill {
    height: 100%;
    background: linear-gradient(90deg, $p-point 0%, $p-point-deep 100%);
    border-radius: 999px;
    transition: width 0.4s ease;
  }
}

/* Time strip (start / end / remaining) */
.p-timestrip {
  display: flex;
  align-items: stretch;
  background: $p-panel;
  border: 1px solid $p-line;
  border-radius: 12px;
  padding: 4px;
  overflow: hidden;

  @media (max-width: 768px) {
    flex-direction: column;
  }

  &__item {
    flex: 1;
    padding: 12px 18px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    border-radius: 8px;

    &--accent {
      background: $p-panel-alt;
    }
  }
  &__divider {
    width: 1px;
    background: $p-line-soft;
    margin: 8px 0;
    flex-shrink: 0;

    @media (max-width: 768px) {
      width: 100%;
      height: 1px;
      margin: 0 8px;
    }
  }
  &__label {
    font-size: 11px;
    font-weight: 800;
    color: $p-ink-soft;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  &__value {
    font-size: 14px;
    font-weight: 700;
    color: $p-ink;

    &--lg {
      font-size: 20px;
      color: $p-err;
      letter-spacing: -0.01em;
    }
  }
}

.p-live-dot--warn { background: $p-warn; }

/* WS 연결 배지 */
.p-ws-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: none;

  &__dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }

  &--on {
    background: $p-ok-soft;
    color: $p-ok;
    .p-ws-badge__dot { animation: p-live-pulse 1.6s infinite; }
  }
  &--off {
    background: $p-warn-soft;
    color: $p-warn;
  }
}

/* 실시간 이벤트 피드 */
.p-feed {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  max-height: 280px;
  overflow-y: auto;

  &__item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 4px;
    border-bottom: 1px solid $p-line-soft;

    &:last-child { border-bottom: none; }
  }
  &__time {
    flex-shrink: 0;
    color: $p-ink-soft;
  }
  &__name {
    font-size: 13px;
    font-weight: 700;
    color: $p-ink;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.p-status-cell {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: center;
}

/* 마감 대기 배너 */
.p-closing-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 18px;
  background: $p-panel-alt;
  border: 1px solid $p-warn;
  border-radius: 12px;

  &__main {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }
  &__text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;

    strong {
      font-size: 13px;
      font-weight: 800;
      color: $p-ink;
    }
    span {
      font-size: 12px;
      color: $p-ink-mid;
    }
  }
}

.p-live-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: $p-err;
  animation: p-live-pulse 1.6s infinite;
}
@keyframes p-live-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.45; transform: scale(1.25); }
}

.p-code {
  font-family: $p-font-mono;
  font-size: 11px;
  padding: 2px 6px;
  background: $p-panel-alt;
  border: 1px solid $p-line-soft;
  border-radius: 4px;
  color: $p-ink-mid;
}

.p-mono { font-family: $p-font-mono; font-size: 12px; color: $p-ink2; }

.p-cell-class {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.p-cell-title { font-size: 13px; font-weight: 700; color: $p-ink; }
.p-cell-sub { font-size: 11px; color: $p-ink-mid; }
.p-leave-count { font-family: $p-font-mono; font-size: 13px; font-weight: 800; color: $p-err; }

.p-user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: $p-point-soft;
  color: $p-ink;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 800;
  flex-shrink: 0;
}

.p-state {
  padding: 40px 0;
  text-align: center;
  color: $p-ink-mid;
  font-size: 13px;
  p { margin: 12px 0 0; }
}
.p-spinner {
  display: inline-block;
  width: 36px; height: 36px;
  border: 3px solid $p-line; border-top-color: $p-point;
  border-radius: 50%;
  animation: p-spin 0.8s linear infinite;
}
@keyframes p-spin { to { transform: rotate(360deg); } }

.p-end-confirm {
  padding: 8px 0 4px;
  text-align: center;
  &__title {
    font-size: 15px;
    font-weight: 800;
    color: $p-ink;
    margin-bottom: 8px;
  }
  &__desc {
    margin: 0;
    font-size: 12px;
    color: $p-ink-mid;
    line-height: 1.5;
  }
}
</style>
